import * as React from 'react'
import { connect } from 'react-redux'
import { TraceSummary } from './domain/TraceSummary'
import Loading from './helper/loading'
import Profile from './monitor/profile'
import { ApplicationState } from './redux/ApplicationState'
import { fetchTraceHistory, FetchTraceHistoryParameters } from './redux/V2/Trace/actions'
import { areTraceHistorySummariesLoaded, getHistoryTraceSummaries } from './redux/V2/Trace/TraceState'
import TraceNavigation from './trace-navigation'
import { Alert } from './ui/alert'
import { AlertTitle } from './ui/alertTitle'
import { Box, BoxBody } from './ui/box'
import { Col, Row } from './ui/grid'

interface DispatchProps {
    fetchTraceHistory: (params: FetchTraceHistoryParameters) => void
}

interface ReduxStateProps {
    traces: TraceSummary[]
    loaded: boolean
    service: string
    environment: string
    showTracepoints: boolean
}

type Props = DispatchProps & ReduxStateProps

class RecentlyViewedByYouComponent extends React.PureComponent<Props> {
    public componentDidMount(): void {
        const { environment, service } = this.props

        this.props.fetchTraceHistory({ environment, service })
    }

    public componentDidUpdate(prevProps: Readonly<Props>) {
        const { environment, service } = this.props
        if (prevProps.environment === environment && prevProps.service === service) {
            return
        }

        this.props.fetchTraceHistory({ environment, service })
    }

    public render() {
        if (!this.props.loaded) {
            return (
                <React.Fragment>
                    <TraceNavigation active="history" showTracepoints={this.props.showTracepoints} />
                    <Loading />
                </React.Fragment>
            )
        }

        if (this.props.traces.length === 0) {
            return (
                <React.Fragment>
                    <TraceNavigation active="history" showTracepoints={this.props.showTracepoints} />
                    <Box>
                        <BoxBody>
                            {this.props.service === '__all' ? (
                                <Row>
                                    <Col width={6}>
                                        <Alert level="info">
                                            <AlertTitle icon="info"> No recently viewed traces</AlertTitle>
                                            <p>
                                                You haven&apos;t viewed any traces recently, which leaves this screen empty. Head over to
                                                the profiles search screen or on any transactions details page to see traces. Once you have
                                                visited one or more traces, they will be listed here for quick access.
                                            </p>
                                        </Alert>
                                    </Col>
                                </Row>
                            ) : (
                                <Row>
                                    <Col width={6}>
                                        <Alert level={'info'}>
                                            <AlertTitle icon="info"> No recently viewed traces for this service</AlertTitle>
                                            <p>
                                                You haven&apos;t viewed any traces for service <strong>{this.props.service} </strong>
                                                and environment <strong>{this.props.environment}</strong> recently, which leaves this screen
                                                empty. Select <strong>All</strong> services to see if history data is available for other
                                                services or navigate to traces on the profiles search screen or any transactions details
                                                page.
                                            </p>
                                        </Alert>
                                    </Col>
                                </Row>
                            )}
                        </BoxBody>
                    </Box>
                </React.Fragment>
            )
        }
        return (
            <React.Fragment>
                <TraceNavigation active="history" showTracepoints={this.props.showTracepoints} />
                <Box>
                    <BoxBody noPadding>
                        <table className="table table-condensed profile-list">
                            {this.props.traces.map(trace => (
                                <Profile key={trace.id} profile={trace} />
                            ))}
                        </table>
                    </BoxBody>
                </Box>
            </React.Fragment>
        )
    }
}

export const RecentlyViewedByYouScreen = connect<ReduxStateProps, DispatchProps>(
    (globalState: ApplicationState, props: Props): ReduxStateProps => ({
        traces: getHistoryTraceSummaries(globalState),
        loaded: areTraceHistorySummariesLoaded(globalState),
        service: globalState.tidewaysContext.service,
        environment: globalState.tidewaysContext.environment,
        showTracepoints: globalState.tidewaysContext.application.monitoring,
    }),
    { fetchTraceHistory },
)(RecentlyViewedByYouComponent)
